import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import { useAuth } from '../../../contexts/AuthContext';
import { useWatchedEpisodes } from '../../../hooks/useWatchedEpisodes';
import { mockDramas, getRelatedDramas, getDramaByFullSlug } from '../../../mocks/dramas';
import { createDramaUrl, generateBreadcrumbs } from '../../../hooks/useNavigation';
import ShareToMessageModal, { SharedContent } from '../../../components/base/ShareToMessageModal';
import { encodeSharedContent } from '../../../components/base/SharedContentCard';
import { useMessages } from '../../../hooks/useMessages';
import { AuthModal } from '../../../components/feature/AuthModal';
import { useWatchlist } from '../../../hooks/useWatchlist';
import { createDramaUrl as createDramaUrlForWatchlist } from '../../../hooks/useNavigation';

interface Drama {
  id: number;
  title: string;
  description: string;
  poster: string;
  banner: string;
  rating: number;
  episodes: number;
  year: number;
  genre: string;
  views: string;
  status: 'ongoing' | 'completed' | 'upcoming';
  country: string;
  language: string;
  director: string;
  cast: string[];
  duration: string;
  releaseDate: string;
  tags: string[];
  synopsis: string;
  trailer?: string;
}

interface Episode {
  id: number;
  number: number;
  title: string;
  description: string;
  duration: string;
  releaseDate: string;
  thumbnail: string;
  views: string;
}

interface Review {
  id: number;
  user: string;
  avatar: string;
  rating: number;
  comment: string;
  date: string;
  likes: number;
}

const DramaDetailPage = () => {
  const { urlParam } = useParams<{ urlParam: string }>();
  const { user } = useAuth();
  const { isInWatchlist, toggleWatchlist, removeFromWatchlist } = useWatchlist();
  
  // Find drama by full slug (genreSlug-titleSlug)
  const drama = useMemo(() => {
    if (!urlParam) return null;
    return getDramaByFullSlug(urlParam);
  }, [urlParam]);

  // Get related dramas
  const relatedDramas = useMemo(() => {
    if (!drama) return [];
    return getRelatedDramas(drama.id, 3);
  }, [drama]);

  // Generate breadcrumbs
  const breadcrumbs = useMemo(() => {
    if (!drama) return [];
    return generateBreadcrumbs('drama', { title: drama.title });
  }, [drama]);
  
  const { 
    watchedEpisodes, 
    loading: watchedLoading, 
    markAsWatched, 
    markAsUnwatched, 
    isEpisodeWatched, 
    getWatchProgress,
    markAllAsWatched,
    clearAllWatched 
  } = useWatchedEpisodes(drama ? parseInt(drama.id.replace('drama-', '')) : 0);
  
  const [episodes, setEpisodes] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('episodes');
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState('');
  const [sortReviews, setSortReviews] = useState<'recent' | 'helpful' | 'rating'>('recent');
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [markingEpisode, setMarkingEpisode] = useState<number | null>(null);
  const [showShareToMessage, setShowShareToMessage] = useState(false);
  const [shareToMessageContent, setShareToMessageContent] = useState<SharedContent | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [watchlistToast, setWatchlistToast] = useState<{ show: boolean; added: boolean }>({ show: false, added: false });

  const { sendMessage, startConversation, fetchConversations: fetchMsgConversations } = useMessages();

  useEffect(() => {
    if (!drama) {
      setLoading(false);
      return;
    }

    // Generate mock episodes based on drama
    const mockEpisodes = Array.from({ length: drama.episodes }, (_, i) => ({
      id: i + 1,
      number: i + 1,
      title: `Episode ${i + 1}`,
      description: `Episode ${i + 1} of ${drama.title} continues the captivating story with new developments and emotional moments.`,
      duration: drama.duration,
      releaseDate: new Date(new Date(drama.releaseDate).getTime() + i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      thumbnail: `https://public.readdy.ai/ai/img_res/0d0d2d9871704878d9075fdbe3dc9e24.jpg$%7Bdrama.title%7D%20episode%20$%7Bi%20%20%201%7D%20Middle%20Eastern%20drama%20cinematic%20still%20with%20professional%20lighting&width=300&height=200&seq=ep${drama.id}${i + 1}&orientation=landscape`,
      views: `${Math.floor(Math.random() * 500 + 200)}K`
    }));

    const mockReviews = [
      {
        id: 1,
        user: 'Amira K.',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20Middle%20Eastern%20woman%20headshot%20with%20warm%20smile%20user%20avatar%20style%20clean%20white%20background&width=100&height=100&seq=rev1&orientation=squarish',
        rating: 5,
        comment: `Absolutely loved ${drama.title}! The portrayal is so authentic and inspiring. The character development is exceptional.`,
        date: '2024-03-15',
        likes: 24
      },
      {
        id: 2,
        user: 'Mohammed S.',
        avatar: 'https://readdy.ai/api/search-image?query=Professional%20Middle%20Eastern%20man%20headshot%20with%20confident%20expression%20user%20avatar%20style%20clean%20white%20background&width=100&height=100&seq=rev2&orientation=squarish',
        rating: 4,
        comment: 'Great storytelling and beautiful cinematography. The setting looks stunning in this series.',
        date: '2024-03-10',
        likes: 18
      },
      {
        id: 3,
        user: 'Fatima A.',
        avatar: 'https://readdy.ai/api/search-image?query=Young%20Middle%20Eastern%20woman%20headshot%20with%20bright%20smile%20user%20avatar%20style%20clean%20white%20background&width=100&height=100&seq=rev3&orientation=squarish',
        rating: 5,
        comment: 'This drama really speaks to the experiences of modern Arab life. The struggles are portrayed so realistically.',
        date: '2024-03-08',
        likes: 31
      }
    ];

    setEpisodes(mockEpisodes);
    setReviews(mockReviews);
    setLoading(false);
  }, [drama]);

  const handleSubmitReview = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (userRating === 0 || userReview.trim() === '') return;

    const newReview = {
      id: reviews.length + 1,
      user: 'You',
      avatar: 'https://readdy.ai/api/search-image?query=Default%20user%20avatar%20neutral%20expression%20simple%20style%20clean%20white%20background&width=100&height=100&seq=defaultuser&orientation=squarish',
      rating: userRating,
      comment: userReview,
      date: new Date().toISOString().split('T')[0],
      likes: 0
    };

    setReviews([newReview, ...reviews]);
    setUserRating(0);
    setUserReview('');
  };

  const getSortedAndFilteredReviews = () => {
    let filtered = [...reviews];
    
    if (filterRating) {
      filtered = filtered.filter(r => r.rating === filterRating);
    }
    
    switch (sortReviews) {
      case 'helpful':
        return filtered.sort((a, b) => b.likes - a.likes);
      case 'rating':
        return filtered.sort((a, b) => b.rating - a.rating);
      case 'recent':
      default:
        return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
  };

  const getRatingDistribution = () => {
    const distribution = [0, 0, 0, 0, 0];
    reviews.forEach(review => {
      distribution[review.rating - 1]++;
    });
    return distribution;
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return drama?.rating.toFixed(1) || '0';
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const handleToggleWatched = async (episodeNumber: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      alert('Please sign in to track your watched episodes');
      return;
    }
    
    setMarkingEpisode(episodeNumber);
    
    if (isEpisodeWatched(episodeNumber)) {
      await markAsUnwatched(episodeNumber);
    } else {
      await markAsWatched(episodeNumber);
    }
    
    setMarkingEpisode(null);
  };

  const handleMarkAllWatched = async () => {
    if (!user) {
      alert('Please sign in to track your watched episodes');
      return;
    }
    await markAllAsWatched(episodes.length);
  };

  const handleClearAllWatched = async () => {
    if (!user) return;
    await clearAllWatched();
  };

  const handleShareDramaToMessage = () => {
    if (!drama) return;
    setShareToMessageContent({
      type: 'drama',
      id: drama.id,
      title: drama.title,
      description: drama.description,
      image: drama.poster,
      url: createDramaUrl(drama.genreSlug, drama.slug),
      meta: {
        rating: drama.rating,
        year: drama.year,
        episodes: drama.episodes,
        genre: drama.genre.join(', ')
      }
    });
    setShowShareToMessage(true);
  };

  const handleSendSharedContent = async (conversationId: string, content: SharedContent) => {
    const encoded = encodeSharedContent(content);
    await sendMessage(conversationId, encoded);
  };

  const handleStartNewConvWithContent = async (userId: string, content: SharedContent) => {
    const convId = await startConversation(userId);
    if (convId) {
      const encoded = encodeSharedContent(content);
      await sendMessage(convId, encoded);
      await fetchMsgConversations();
    }
  };

  const inWatchlist = drama ? isInWatchlist(drama.id) : false;

  const handleToggleWatchlist = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!drama) return;

    const added = toggleWatchlist({
      id: drama.id,
      type: 'drama',
      title: drama.title,
      poster: drama.poster,
      rating: drama.rating,
      genre: drama.genre.join(', '),
      year: drama.year,
      episodes: drama.episodes,
      duration: drama.duration,
      progress: 0,
      totalEpisodes: drama.episodes,
      url: createDramaUrlForWatchlist(drama.genreSlug, drama.slug),
    });

    setWatchlistToast({ show: true, added });
    setTimeout(() => setWatchlistToast({ show: false, added: false }), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="animate-pulse">
          <div className="h-96 bg-gray-300"></div>
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-2/3 mb-8"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!drama) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <i className="ri-film-line text-6xl text-gray-300 mb-4"></i>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Drama Not Found</h1>
          <p className="text-gray-600 mb-6">The drama you're looking for doesn't exist.</p>
          <Link to="/dramas" className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors">
            Browse All Dramas
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ratingDistribution = getRatingDistribution();
  const averageRating = getAverageRating();
  const watchProgress = getWatchProgress();
  const progressPercentage = episodes.length > 0 ? (watchProgress / episodes.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Watchlist Toast */}
      {watchlistToast.show && (
        <div className="fixed top-20 right-6 z-50 animate-slide-in">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border ${
            watchlistToast.added
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-gray-50 border-gray-200 text-gray-800'
          }`}>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
              watchlistToast.added ? 'bg-emerald-100' : 'bg-gray-100'
            }`}>
              <i className={`${watchlistToast.added ? 'ri-bookmark-fill text-emerald-600' : 'ri-bookmark-line text-gray-500'}`}></i>
            </div>
            <span className="text-sm font-medium">
              {watchlistToast.added ? 'Added to your watchlist!' : 'Removed from your watchlist'}
            </span>
          </div>
        </div>
      )}
      
      {/* Back Button + Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <Link
            to="/dramas"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dramas
          </Link>
          <nav className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center">
                {index > 0 && <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>}
                {crumb.href === '#' ? (
                  <span className="text-gray-900 font-medium">{crumb.label}</span>
                ) : (
                  <Link to={crumb.href} className="text-gray-500 hover:text-rose-600 transition-colors">
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Hero Banner */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={drama.banner}
          alt={drama.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end space-x-6">
              <img
                src={drama.poster}
                alt={drama.title}
                className="w-48 h-72 object-cover object-top rounded-lg shadow-2xl"
              />
              
              <div className="flex-1 text-white">
                <h1 className="text-4xl md:text-5xl font-bold mb-2">{drama.title}</h1>
                <p className="text-xl text-gray-200 mb-4">{drama.description}</p>
                
                <div className="flex items-center space-x-6 mb-4">
                  <div className="flex items-center">
                    <i className="ri-star-fill text-yellow-400 mr-1"></i>
                    <span className="font-semibold">{drama.rating}</span>
                  </div>
                  <span>{drama.year}</span>
                  <span>{drama.episodes} episodes</span>
                  <span>{drama.duration}</span>
                  <span className={`px-3 py-1 rounded-full text-sm ${
                    drama.status === 'ongoing' ? 'bg-green-500' :
                    drama.status === 'completed' ? 'bg-rose-500' :
                    'bg-orange-500'
                  }`}>
                    {drama.status.charAt(0).toUpperCase() + drama.status.slice(1)}
                  </span>
                </div>

                {user && (
                  <div className="mb-4 max-w-md">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-300">Your Progress</span>
                      <span className="text-white font-medium">{watchProgress}/{episodes.length} episodes</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center space-x-4">
                  <button className="px-8 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill mr-2"></i>
                    Watch Now
                  </button>
                  <button
                    onClick={handleToggleWatchlist}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      inWatchlist
                        ? 'bg-emerald-500/90 hover:bg-emerald-600 text-white'
                        : 'bg-white/20 hover:bg-white/30 text-white'
                    }`}
                  >
                    <i className={`${inWatchlist ? 'ri-bookmark-fill' : 'ri-add-line'} mr-2`}></i>
                    {inWatchlist ? 'In Watchlist' : 'Add to Watchlist'}
                  </button>
                  <button
                    onClick={handleShareDramaToMessage}
                    className="px-5 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    title="Send in message"
                  >
                    <i className="ri-message-3-line mr-2"></i>
                    Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'episodes', label: 'Episodes', icon: 'ri-play-list-line' },
                    { id: 'overview', label: 'Overview', icon: 'ri-information-line' },
                    { id: 'reviews', label: `Reviews (${reviews.length})`, icon: 'ri-star-line' }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === tab.id
                          ? 'border-rose-500 text-rose-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={`${tab.icon} mr-2`}></i>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Episodes Tab */}
                {activeTab === 'episodes' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-900">All Episodes</h2>
                      {user && (
                        <div className="flex items-center space-x-2">
                          {watchProgress < episodes.length ? (
                            <button
                              onClick={handleMarkAllWatched}
                              className="px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-checkbox-circle-line mr-1"></i>
                              Mark All Watched
                            </button>
                          ) : (
                            <button
                              onClick={handleClearAllWatched}
                              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-refresh-line mr-1"></i>
                              Reset Progress
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Grid Layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {episodes.map((episode) => {
                        const watched = isEpisodeWatched(episode.number);
                        const isMarking = markingEpisode === episode.number;
                        
                        return (
                          <div 
                            key={episode.id} 
                            className={`rounded-lg overflow-hidden transition-all cursor-pointer group ${
                              watched ? 'bg-green-50 border-2 border-green-300' : 'bg-white border border-gray-200 hover:border-rose-300 hover:shadow-md'
                            }`}
                          >
                            {/* Thumbnail */}
                            <div className="relative aspect-video">
                              <img
                                src={episode.thumbnail}
                                alt={episode.title}
                                className={`w-full h-full object-cover object-center ${watched ? 'opacity-70' : ''}`}
                              />
                              
                              {/* Play Overlay on Hover */}
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                                  <i className="ri-play-fill text-white text-3xl"></i>
                                </div>
                              </div>
                              
                              {/* Watched Badge */}
                              {watched && (
                                <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                  <i className="ri-check-line text-white text-lg"></i>
                                </div>
                              )}
                              
                              {/* Episode Number Badge */}
                              <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs font-semibold rounded">
                                EP {episode.number}
                              </div>
                            </div>
                            
                            {/* Episode Info */}
                            <div className="p-4">
                              <h3 className={`font-semibold text-base mb-2 line-clamp-2 ${watched ? 'text-green-800' : 'text-gray-900'}`}>
                                {episode.title}
                              </h3>
                              
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                {episode.description}
                              </p>
                              
                              {/* Episode Meta */}
                              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                <div className="flex items-center space-x-3">
                                  <span className="flex items-center">
                                    <i className="ri-time-line mr-1"></i>
                                    {episode.duration}
                                  </span>
                                  <span className="flex items-center">
                                    <i className="ri-eye-line mr-1"></i>
                                    {episode.views}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="text-xs text-gray-500 mb-3">
                                <i className="ri-calendar-line mr-1"></i>
                                {episode.releaseDate}
                              </div>
                              
                              {/* Watch Status Button */}
                              {user && (
                                <button
                                  onClick={(e) => handleToggleWatched(episode.number, e)}
                                  disabled={isMarking}
                                  className={`w-full flex items-center justify-center px-3 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                                    watched 
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  } ${isMarking ? 'opacity-50' : ''}`}
                                >
                                  {isMarking ? (
                                    <>
                                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                                      Processing...
                                    </>
                                  ) : watched ? (
                                    <>
                                      <i className="ri-checkbox-circle-fill mr-2"></i>
                                      Watched
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-checkbox-blank-circle-line mr-2"></i>
                                      Mark as Watched
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Synopsis</h3>
                      <p className="text-gray-700 leading-relaxed">{drama.synopsis}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Cast & Crew</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Director</h4>
                          <p className="text-gray-700">{drama.director}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Main Cast</h4>
                          <div className="space-y-1">
                            {drama.cast.map((actor, index) => (
                              <p key={index} className="text-gray-700">{actor}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {drama.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-lg p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="text-center">
                          <div className="text-5xl font-bold text-gray-900 mb-2">{averageRating}</div>
                          <div className="flex items-center justify-center mb-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i
                                key={star}
                                className={`ri-star-fill text-xl ${
                                  star <= Math.round(parseFloat(averageRating)) ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              ></i>
                            ))}
                          </div>
                          <p className="text-sm text-gray-600">{reviews.length} reviews</p>
                        </div>
                        <div className="space-y-2">
                          {[5, 4, 3, 2, 1].map((rating) => (
                            <div key={rating} className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600 w-8">{rating} <i className="ri-star-fill text-xs text-yellow-400"></i></span>
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-yellow-400 rounded-full"
                                  style={{ width: `${reviews.length > 0 ? (ratingDistribution[rating - 1] / reviews.length) * 100 : 0}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600 w-8">{ratingDistribution[rating - 1]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Write Review */}
                    {user ? (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Write a Review</h3>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setUserRating(star)}
                                className={`text-2xl cursor-pointer ${
                                  star <= userRating ? 'text-yellow-400' : 'text-gray-300'
                                } hover:text-yellow-400 transition-colors`}
                              >
                                <i className="ri-star-fill"></i>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="mb-4">
                          <textarea
                            value={userReview}
                            onChange={(e) => setUserReview(e.target.value)}
                            placeholder="Share your thoughts..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 resize-none text-sm"
                            rows={4}
                            maxLength={500}
                          />
                          <p className="text-xs text-gray-500 mt-1">{userReview.length}/500</p>
                        </div>
                        <button
                          onClick={handleSubmitReview}
                          disabled={userRating === 0 || userReview.trim() === ''}
                          className="px-6 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-400 text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Submit Review
                        </button>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-6 text-center">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-rose-100 flex items-center justify-center">
                          <i className="ri-lock-line text-2xl text-rose-500"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Sign in to Write a Review</h3>
                        <p className="text-sm text-gray-500 mb-4">Share your thoughts and rate this drama after signing in.</p>
                        <button
                          onClick={() => setShowAuthModal(true)}
                          className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-login-box-line mr-2"></i>
                          Sign In
                        </button>
                      </div>
                    )}

                    {/* Reviews List */}
                    <div className="space-y-4">
                      {getSortedAndFilteredReviews().map((review) => (
                        <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <img src={review.avatar} alt={review.user} className="w-12 h-12 rounded-full object-cover" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900">{review.user}</h4>
                                <span className="text-sm text-gray-500">{review.date}</span>
                              </div>
                              <div className="flex text-yellow-400 mb-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <i
                                    key={star}
                                    className={`ri-star-fill text-sm ${
                                      star <= review.rating ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                  ></i>
                                ))}
                              </div>
                              <p className="text-gray-700 mb-3">{review.comment}</p>
                              <button
                                onClick={() => {
                                  if (!user) {
                                    setShowAuthModal(true);
                                    return;
                                  }
                                  // TODO: like logic
                                }}
                                className="text-sm text-gray-500 hover:text-rose-600 transition-colors cursor-pointer"
                              >
                                <i className="ri-thumb-up-line mr-1"></i>
                                Helpful ({review.likes})
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Drama Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Country:</span>
                  <span className="font-medium">{drama.country}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Language:</span>
                  <span className="font-medium">{drama.language}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Genre:</span>
                  <span className="font-medium">{drama.genre.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Episodes:</span>
                  <span className="font-medium">{drama.episodes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">{drama.duration}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Release Date:</span>
                  <span className="font-medium">{drama.releaseDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Views:</span>
                  <span className="font-medium">{drama.views}</span>
                </div>
              </div>
            </div>

            {/* Related Dramas */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Dramas</h3>
              <div className="space-y-4">
                {relatedDramas.map((relatedDrama) => (
                  <Link
                    key={relatedDrama.id}
                    to={createDramaUrl(relatedDrama.genreSlug, relatedDrama.slug)}
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <img
                      src={relatedDrama.poster}
                      alt={relatedDrama.title}
                      className="w-16 h-20 object-cover object-top rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 line-clamp-1">{relatedDrama.title}</h4>
                      <p className="text-sm text-gray-600 line-clamp-2">{relatedDrama.description}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">{relatedDrama.year}</span>
                        <div className="flex items-center text-xs text-gray-500">
                          <i className="ri-star-fill text-yellow-400 mr-1"></i>
                          {relatedDrama.rating}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <ShareToMessageModal
        isOpen={showShareToMessage}
        onClose={() => setShowShareToMessage(false)}
        content={shareToMessageContent}
        onSend={handleSendSharedContent}
        onStartNewConversation={handleStartNewConvWithContent}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default DramaDetailPage;